import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation,
  signal
} from "@angular/core";
import { CommonModule } from "@angular/common";

@Component({
  selector: "app-video-player",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./video-player.component.html",
  styles: [
    `
      app-video-player {
        display: block;
        width: 100%;
        height: 100%;
        --video-player-track: rgba(120, 120, 120, 0.2);
      }

      .dark app-video-player,
      :host-context(.dark) {
        --video-player-track: rgba(255, 255, 255, 0.15);
      }

      /* Native elements styled in fullscreen */
      .video-player-container:fullscreen,
      app-video-player:fullscreen {
        background-color: #000;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      }

      .video-player-container:fullscreen video {
        object-fit: contain !important;
      }

      .video-player-slider {
        min-width: 0;
      }

      .video-player-slider::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: var(--fuse-primary, #2196F3);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
        transition: transform 0.15s ease-in-out;
      }

      .video-player-slider:hover::-webkit-slider-thumb {
        transform: scale(1.3);
      }

      .video-player-slider::-moz-range-thumb {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: var(--fuse-primary, #2196F3);
        border: none;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
        transition: transform 0.15s ease-in-out;
      }

      .video-player-slider:hover::-moz-range-thumb {
        transform: scale(1.3);
      }
    `
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoPlayerComponent implements AfterViewInit, OnDestroy {
  @Input() src: string = "";
  @Input() autoplay: boolean = false;
  @Input() muted: boolean = false;
  @Input() loop: boolean = true;
  @Input() poster: string = "";

  @Output() loadedmetadata = new EventEmitter<void>();

  @ViewChild("videoElement") videoElement!: ElementRef<HTMLVideoElement>;

  public isPlaying = signal(false);
  public isFullscreen = signal(false);
  public currentTime = signal(0);
  public duration = signal(0);

  // Inactivity tracking for premium overlay behavior
  public userActive = signal(false);
  private activeTimeout: any = null;

  public onMouseMove(): void {
    this.userActive.set(true);
    if (this.activeTimeout) {
      clearTimeout(this.activeTimeout);
    }
    this.activeTimeout = setTimeout(() => {
      this.userActive.set(false);
    }, 2500); // Hide controls after 2.5 seconds of mouse inactivity
  }

  public onMouseLeave(): void {
    this.userActive.set(false);
    if (this.activeTimeout) {
      clearTimeout(this.activeTimeout);
      this.activeTimeout = null;
    }
  }

  ngAfterViewInit(): void {
    const video = this.videoElement.nativeElement;
    // Safely sync initial playback state (handles autoplay)
    setTimeout(() => {
      this.isPlaying.set(!video.paused);
    });
  }

  ngOnDestroy(): void {
    if (this.activeTimeout) {
      clearTimeout(this.activeTimeout);
    }
  }

  public togglePlay(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const video = this.videoElement.nativeElement;
    if (video.paused) {
      video.play().catch(err => console.error("Error playing video:", err));
    } else {
      video.pause();
    }
  }

  public onPlayStateChange(playing: boolean): void {
    this.isPlaying.set(playing);
  }

  public onLoadedMetadata(): void {
    const video = this.videoElement.nativeElement;
    this.duration.set(video.duration || 0);
    this.loadedmetadata.emit();
  }

  public onTimeUpdate(): void {
    const video = this.videoElement.nativeElement;
    this.currentTime.set(video.currentTime || 0);
    
    // Ensure play/pause state is always dynamically synchronized with actual video element state
    if (this.isPlaying() === video.paused) {
      this.isPlaying.set(!video.paused);
    }
  }

  public onSeek(event: Event): void {
    event.stopPropagation();
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    const video = this.videoElement.nativeElement;
    video.currentTime = value;
    this.currentTime.set(value);
  }

  public toggleFullscreen(event: Event): void {
    event.stopPropagation();
    // Request fullscreen on host element or the relative container
    const container = this.videoElement.nativeElement.parentElement;
    if (!container) return;

    if (!document.fullscreenElement) {
      container.requestFullscreen().catch(err => {
        console.error("Error entering fullscreen:", err);
      });
    } else {
      document.exitFullscreen();
    }
  }

  @HostListener("document:fullscreenchange")
  public onFullscreenChange(): void {
    const container = this.videoElement.nativeElement.parentElement;
    this.isFullscreen.set(document.fullscreenElement === container);
  }

  @HostListener("window:keydown", ["$event"])
  public handleKeyDown(event: KeyboardEvent): void {
    // Spacebar toggles play/pause
    if (event.code === "Space") {
      const activeEl = document.activeElement;
      if (activeEl) {
        const tagName = activeEl.tagName.toLowerCase();
        if (
          tagName === "input" ||
          tagName === "textarea" ||
          activeEl.hasAttribute("contenteditable") ||
          tagName === "select" ||
          activeEl.classList.contains("video-player-slider")
        ) {
          return;
        }
      }

      // Check visibility of this player
      const rect = this.videoElement.nativeElement.getBoundingClientRect();
      const isVisible = rect.width > 0 && rect.height > 0;
      if (!isVisible) return;

      const container = this.videoElement.nativeElement.parentElement;
      const isHovered = container?.matches(":hover");
      const isFocused = container === activeEl || container?.contains(activeEl);

      // Trigger toggle if hovered, focused, or already playing
      if (this.isPlaying() || isHovered || isFocused) {
        event.preventDefault();
        this.togglePlay();
      }
    }
  }

  public formatTime(seconds: number): string {
    if (!seconds || isNaN(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }

  public getScrubberBackground(): string {
    const curr = this.currentTime();
    const dur = this.duration();
    const percent = dur > 0 ? (curr / dur) * 100 : 0;
    
    // We dynamically style the range slider's linear gradient background
    // Filled progress part uses theme's primary color (var(--fuse-primary))
    // Track part uses theme's border/track color (var(--video-player-track))
    return `linear-gradient(to right, var(--fuse-primary, #2196F3) 0%, var(--fuse-primary, #2196F3) ${percent}%, var(--video-player-track, rgba(120,120,120,0.2)) ${percent}%, var(--video-player-track, rgba(120,120,120,0.2)) 100%)`;
  }
}
